import { catalogById } from "../catalog";
import type { Field, FamiliarityRecord, GameScope, SetRecord } from "../types";

export const RECENT_SET_LIMIT = 20;

export function filterByScope(sets: readonly SetRecord[], scope: GameScope): SetRecord[] {
  if (scope.kind === "all") return [...sets];
  if (scope.kind === "eras") {
    const ids = new Set(scope.eraIds);
    return sets.filter((set) => ids.has(set.eraId));
  }
  const ids = new Set(scope.setIds);
  return sets.filter((set) => ids.has(set.id));
}

export function getScopeCandidates(scope: GameScope, sets: readonly SetRecord[]): SetRecord[] {
  return filterByScope(sets, scope).filter((set) => catalogById.has(set.id));
}

export function requestedFamiliarity(set: SetRecord, fields: readonly Field[], familiarity: Record<string, FamiliarityRecord>): number {
  if (fields.length === 0) return 0;
  const record = familiarity[set.id];
  if (!record) return 0;
  return fields.reduce((sum, field) => sum + Math.min(1, Math.max(0, record[field] ?? 0)), 0) / fields.length;
}

export function selectionWeight(familiarity: number): number {
  const safe = Math.min(1, Math.max(0, Number.isFinite(familiarity) ? familiarity : 0));
  return 1 + 4 * (1 - safe) ** 2;
}

export function chooseWeighted<T>(items: readonly T[], weights: readonly number[], random = Math.random): T | null {
  if (items.length === 0 || items.length !== weights.length) return null;
  const total = weights.reduce((sum, weight) => sum + Math.max(0, weight), 0);
  if (total <= 0) return items[items.length - 1] ?? null;
  const target = Math.min(0.999999999999, Math.max(0, random())) * total;
  let cumulative = 0;
  for (let index = 0; index < items.length; index += 1) {
    cumulative += Math.max(0, weights[index]);
    if (target < cumulative) return items[index];
  }
  return items[items.length - 1] ?? null;
}

export function chooseNextSet(
  sets: readonly SetRecord[],
  scope: GameScope,
  fields: readonly Field[],
  familiarity: Record<string, FamiliarityRecord>,
  recentSetIds: readonly string[] = [],
  random = Math.random,
): SetRecord | null {
  let candidates = filterByScope(sets, scope);
  if (candidates.length > 1) {
    const recent = new Set(recentSetIds.slice(-RECENT_SET_LIMIT));
    const withoutRecent = candidates.filter((set) => !recent.has(set.id));
    if (withoutRecent.length > 0) candidates = withoutRecent;
  }
  const weights = candidates.map((set) => selectionWeight(requestedFamiliarity(set, fields, familiarity)));
  return chooseWeighted(candidates, weights, random);
}
